import React, { useEffect, useState } from "react"
import { Button, Col, ControlLabel, FormControl, FormGroup, Modal, Row } from "react-bootstrap"
import axios from "axios"
import getServerURL from "../../../../../functions/getServerURL"
import LoadingAnimation from "../../../../../reuseable-components/LoadingAnimation"
import { handleErrorFetchingData } from "../../../../../functions/errorHandling"
import GroupMultiSelect from "./GroupMultiSelect"
import swal from "sweetalert"

import ExportFunctionalGroupsForTraining from "../ExportFunctionalGroupsForTraining"


export default function AssignToGroupModal({ training }) {
  // STATE
  const [show, toggleShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [assignment, setAssignment] = useState(null)
  const [functionalGroups, setFunctionalGroups] = useState([])
  const [selectedFunctionalGroups, setSelectedFunctionalGroups] = useState([])
  const [assignedFunctionalGroups, setAssignedFunctionalGroups] = useState([])
  const [selectedAssignedFunctionalGroups, setSelectedAssignedFunctionalGroups] = useState([])
  const [originalFunctionalGroups, setOriginalFunctionalGroups] = useState([])
  const [reason, setReason] = useState("")
  const [reasonError, setReasonError] = useState("")


  const handleSubmit = () => {
    // Check if functional groups have changed
    const originalFGIds = originalFunctionalGroups.map(fg => fg.id).sort()
    const currentFGIds = assignedFunctionalGroups.map(fg => fg.id).sort()
    const fgChanged = JSON.stringify(originalFGIds) !== JSON.stringify(currentFGIds)

    // If no changes, show info message
    if (!fgChanged) {
      swal({
        title: "No Changes",
        text: "No changes were made to functional group assignments.",
        icon: "info",
        button: "OK",
      })
      return
    }

    // If functional groups changed, validate reason
    setReasonError("")
    if (!reason || reason.trim().length < 10) {
      setReasonError("Reason must be at least 10 characters long")
      return
    }

    // Submit with reason
    swal({
      text: `Updating functional group assignments... Please stand by.`,
      icon: require("../../../../../static/images/spinner.gif"),
      button: false
    })
    setSubmitting(true)
    
    // Prepare request data
    const requestData = {
      functional_groups: assignedFunctionalGroups.map(functionalGroup => functionalGroup.id),
      reason: reason.trim()
    }
    
    // Use assignment_id if available, otherwise use assessment_id (backend will auto-create assignment)
    if (assignment && assignment.id) {
      requestData.assignment_id = assignment.id
    } else if (training.assessment_id) {
      requestData.assessment_id = training.assessment_id
    } else {
      console.error('Neither assignment nor assessment information is available')
      swal({
        title: "Error",
        text: "This training does not have an assessment yet. Please create an assessment first.",
        icon: "error",
        button: "OK",
      })
      setSubmitting(false)
      return
    }
    
    axios.post(`${getServerURL()}assessments/assign_assessment_to_groups/`, requestData, {
      headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
    }).then(response => {
      setSelectedFunctionalGroups([])
      setSelectedAssignedFunctionalGroups([])
      swal({
        title: "Success",
        text: "Functional group assignments have been updated successfully.",
        icon: "success",
        button: "Continue",
      }).then(() => handleClose())
    }).catch(error => {
      console.log(error.response)
      handleErrorFetchingData(error)
    }).finally(() => {
      setSubmitting(false)
    })
  }

  const handleClose = () => {
    toggleShow(false)
    setLoading(false)
    setAssignment(null)
    setFunctionalGroups([])
    setSelectedFunctionalGroups([])
    setAssignedFunctionalGroups([])
    setSelectedAssignedFunctionalGroups([])
    setOriginalFunctionalGroups([])
    setReason("")
    setReasonError("")
  }

  const getFunctionalGroups = () => {
    return axios.get(`${getServerURL()}functional-groups`, {
      params: { "is_active": true, "ordering": "name" },
      headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
    })
  }

  const getAssignment = () => {
    return axios.get(`${getServerURL()}assignments/get_assignment_details/`, {
      params: { assessment: training.assessment_id },
      headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
    })
  }

  const getData = () => {
    setLoading(true)
    axios.all([getFunctionalGroups(), getAssignment()]).then(
      axios.spread((functionalGroupResponse, assignmentResponse) => {
        const assignmentData = assignmentResponse.data[0]
        let functionalGroupData = functionalGroupResponse.data

        setAssignment(assignmentData || null)

        if (assignmentData) {
          setAssignedFunctionalGroups(assignmentData.functional_groups)
          setOriginalFunctionalGroups(assignmentData.functional_groups)
          functionalGroupData = functionalGroupData.filter(({ id: id1 }) => {
            return !assignmentData.functional_groups.some(({ id: id2 }) => id2 === id1)
          })
        }
        setFunctionalGroups(functionalGroupData)
      })
    ).catch(error => {
      console.log(error.response)
      handleErrorFetchingData(error)
    }).finally(() => {
      setLoading(false)
    })
  }


  const transferSelectedFunctionalGroups = (multiselectMode = "add") => {
    if (multiselectMode === "add") {
      let updatedAssignedFunctionalGroupList = [...assignedFunctionalGroups, ...selectedFunctionalGroups].sort((a, b) => a.name.localeCompare(b.name))
      let updatedGenesisFunctionalGroupList = [...functionalGroups].filter(({ id: id1 }) => {
        return !selectedFunctionalGroups.some(({ id: id2 }) => id2 === id1)
      })
      setSelectedFunctionalGroups([])
      setFunctionalGroups(updatedGenesisFunctionalGroupList)
      setAssignedFunctionalGroups(updatedAssignedFunctionalGroupList)
    } else {
      let updatedFunctionalGroupList = [...functionalGroups, ...selectedAssignedFunctionalGroups].sort((a, b) => a.name.localeCompare(b.name))
      let updatedAssignedFunctionalGroupList = [...assignedFunctionalGroups].filter(({ id: id1 }) => {
        return !selectedAssignedFunctionalGroups.some(({ id: id2 }) => id2 === id1)
      })
      setSelectedAssignedFunctionalGroups([])
      setAssignedFunctionalGroups(updatedAssignedFunctionalGroupList)
      setFunctionalGroups(updatedFunctionalGroupList)
    }
  }

  const selectAllFunctionalGroups = (multiselectMode, searchTerm = null) => {
    if (multiselectMode === "add") {
      let updatedGenesisUsers = functionalGroups.filter(option => option.name.toLowerCase().includes(searchTerm))
      setSelectedFunctionalGroups(updatedGenesisUsers)
    } else {
      let updatedAssignedFunctionalGroups = assignedFunctionalGroups.filter(option => option.name.toLowerCase().includes(searchTerm))
      setSelectedAssignedFunctionalGroups(updatedAssignedFunctionalGroups)
    }
  }

  useEffect(() => {
    if (show) {
      getData()
    }
  }, [show])

  return (
    <React.Fragment>
      <Button onClick={ () => toggleShow(true) }>Assign to Group</Button>
      <br />
      <ExportFunctionalGroupsForTraining
        training={training}
      />
      <Modal show={ show } onHide={ handleClose }>
        <Modal.Header closeButton>
          <Modal.Title>Assign to Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          { loading ? <LoadingAnimation />
            :
            <div>
              {/*{ assignment ?*/}
              {/*  <div>*/}
              {/*    This training is assigned to the following groups:*/}
              {/*  </div>*/}
              {/*  :*/}
              {/*  <div>*/}
              {/*    This training has not been assigned yet*/}
              {/*  </div>*/}
              {/*}*/}
              <Row>
                <Col xs={6} sm={6} md={6} lg={6}>
                  <FormGroup controlId="formControlsSelectMultiple">
                    <ControlLabel>Functional Groups</ControlLabel>
                    <GroupMultiSelect
                      onChange={ setSelectedFunctionalGroups }
                      functionalGroups={ functionalGroups }
                      setSelectedFunctionalGroupsFunction={ setSelectedFunctionalGroups }
                      setOppositeFunctionalGroupsFunction={ setAssignedFunctionalGroups }
                      selectedFunctionalGroups={ selectedFunctionalGroups }
                      transferSelectedFunctionalGroups={ transferSelectedFunctionalGroups }
                      selectAllFunctionalGroups={ selectAllFunctionalGroups }
                      />
                  </FormGroup>
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                  <FormGroup controlId="formControlsSelectMultiple">
                    <ControlLabel>Assigned Functional Groups</ControlLabel>
                    <GroupMultiSelect
                      mode="remove"
                      onChange={ setSelectedAssignedFunctionalGroups }
                      functionalGroups={ assignedFunctionalGroups }
                      setSelectedFunctionalGroupsFunction={ setSelectedAssignedFunctionalGroups }
                      setOppositeFunctionalGroupsFunction={ setFunctionalGroups }
                      selectedFunctionalGroups={ selectedAssignedFunctionalGroups }
                      transferSelectedFunctionalGroups={ transferSelectedFunctionalGroups }
                      selectAllFunctionalGroups={ selectAllFunctionalGroups }
                      />
                  </FormGroup>
                </Col>
              </Row>
              
              {/* Show reason field only when functional groups have been modified */}
              {(() => {
                const originalFGIds = originalFunctionalGroups.map(fg => fg.id).sort()
                const currentFGIds = assignedFunctionalGroups.map(fg => fg.id).sort()
                const fgChanged = JSON.stringify(originalFGIds) !== JSON.stringify(currentFGIds)
                
                if (fgChanged) {
                  return (
                    <div style={{ marginTop: '20px' }}>
                      <FormGroup controlId="formControlsReason" validationState={reasonError ? "error" : null}>
                        <ControlLabel>
                          Reason for Change <span style={{ color: 'red' }}>*</span>
                        </ControlLabel>
                        <FormControl
                          componentClass="textarea"
                          rows={3}
                          placeholder="Please provide a reason for this change (minimum 10 characters)"
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value)
                            if (reasonError && e.target.value.trim().length >= 10) {
                              setReasonError("")
                            }
                          }}
                          style={{ resize: 'vertical' }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginTop: '5px',
                          fontSize: '12px'
                        }}>
                          <span style={{ color: reasonError ? '#a94442' : '#999' }}>
                            {reasonError || `${reason.trim().length} / 10 characters minimum`}
                          </span>
                          <span style={{ color: '#999' }}>
                            {reason.length} characters
                          </span>
                        </div>
                      </FormGroup>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={ handleClose }>Close</Button>
          <Button onClick={ handleSubmit } disabled={ submitting } bsStyle="primary">Submit</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/TrainingContainer/AssignToGroupModal/index.js